import { BaseComponent } from '../base-component.js';
import { api, LogEntry, LogMetrics } from '../../client/api.js';
import { escapeHtml } from '../../shared/utils.js';
import componentStyles from './admin-logs.css?inline';

export class AdminLogs extends BaseComponent {
  private logs: LogEntry[] = [];
  private metrics: LogMetrics | null = null;
  private categories: string[] = [];
  private loading = true;
  private selectedLevel = '';
  private selectedCategory = '';
  private searchQuery = '';
  private autoRefresh = false;
  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private rateLimitedUntil: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  disconnectedCallback(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    if (this.loading && !this.metrics) {
      return '<div class="loading">Loading logs...</div>';
    }

    const rateLimitWarning = this.rateLimitedUntil && this.rateLimitedUntil > Date.now()
      ? `<div class="rate-limit-warning">Slow down! Too many requests. Please wait ${Math.ceil((this.rateLimitedUntil - Date.now()) / 1000)}s...</div>`
      : '';

    return `
      <ag-panel>
        <ag-panel-header>
          <h2>System Logs</h2>
          <p class="description">Monitor the system logs in real-time, filter by log level and category, and search for specific entries. This is useful for debugging and keeping an eye on what's happening under the hood.</p>
        </ag-panel-header>
        <ag-panel-content>
          ${rateLimitWarning}
          ${this.renderMetrics()}
          ${this.renderFilters()}
          ${this.renderLogs()}
        </ag-panel-content>
      </ag-panel>
    `;
  }

  private renderMetrics(): string {
    if (!this.metrics) return '';

    return `
      <div class="metrics">
        <ag-card>
          <ag-card-title>User Req/min</ag-card-title>
          <ag-card-content>${this.metrics.userRequestsPerMinute}</ag-card-content>
        </ag-card>
        <ag-card>
          <ag-card-title>Total Req/min</ag-card-title>
          <ag-card-content>${this.metrics.requestsPerMinute}</ag-card-content>
        </ag-card>
        <ag-card class="errors">
          <ag-card-title>Errors (1h)</ag-card-title>
          <ag-card-content>${this.metrics.recentErrors}</ag-card-content>
        </ag-card>
        <ag-card class="warnings">
          <ag-card-title>Warnings</ag-card-title>
          <ag-card-content>${this.metrics.byLevel.WARN || 0}</ag-card-content>
        </ag-card>
        <ag-card class="info">
          <ag-card-title>Total Logs</ag-card-title>
          <ag-card-content>${this.metrics.total}</ag-card-content>
        </ag-card>
      </div>
    `;
  }

  private renderFilters(): string {
    const categoryOptions = this.categories
      .map((cat) => `<option value="${escapeHtml(cat)}" ${this.selectedCategory === cat ? 'selected' : ''}>${escapeHtml(cat)}</option>`)
      .join('');

    return `
      <div class="filters">
        <select id="levelFilter">
          <option value="">All Levels</option>
          <option value="DEBUG" ${this.selectedLevel === 'DEBUG' ? 'selected' : ''}>DEBUG</option>
          <option value="INFO" ${this.selectedLevel === 'INFO' ? 'selected' : ''}>INFO</option>
          <option value="WARN" ${this.selectedLevel === 'WARN' ? 'selected' : ''}>WARN</option>
          <option value="ERROR" ${this.selectedLevel === 'ERROR' ? 'selected' : ''}>ERROR</option>
        </select>
        
        <select id="categoryFilter">
          <option value="">All Categories</option>
          ${categoryOptions}
        </select>
        
        <input class="form-control" type="text" id="searchInput" placeholder="Search logs..." value="${escapeHtml(this.searchQuery)}">
        
        <label>
          <input type="checkbox" id="autoRefresh" ${this.autoRefresh ? 'checked' : ''}>
          Auto-refresh
        </label>
        
        <div class="ag-button-group right">
          <button class="btn btn-secondary refresh-btn" id="refreshBtn">Refresh</button>
        </div>
      </div>
    `;
  }

  private renderLogs(): string {
    if (this.logs.length === 0) {
      return '<div class="empty">No logs found</div>';
    }

    const logEntries = this.logs
      .map((log) => {
        const time = new Date(log.timestamp).toLocaleTimeString();
        const date = new Date(log.timestamp).toLocaleDateString();
        const meta = log.meta ? JSON.stringify(log.meta) : '';

        return `
          <div class="log-entry">
            <span class="log-timestamp">${escapeHtml(date)} ${escapeHtml(time)}</span>
            <span class="log-level ${log.level}">[${log.level}]</span>
            <span class="log-category">[${escapeHtml(log.category)}]</span>
            <span class="log-message">${escapeHtml(log.message)}</span>
            ${meta ? `<span class="log-meta">${escapeHtml(meta)}</span>` : ''}
          </div>
        `;
      })
      .join('');

    return `
      <div>
        <div class="section-title">Recent Logs (${this.logs.length})</div>
        <div class="logs-container">${logEntries}</div>
      </div>
    `;
  }

  protected afterRender(): void {
    const levelFilter = this.$('#levelFilter') as HTMLSelectElement;
    const categoryFilter = this.$('#categoryFilter') as HTMLSelectElement;
    const searchInput = this.$('#searchInput') as HTMLInputElement;
    const autoRefreshCheckbox = this.$('#autoRefresh') as HTMLInputElement;
    const refreshBtn = this.$('#refreshBtn') as HTMLButtonElement;

    levelFilter?.addEventListener('change', () => {
      this.selectedLevel = levelFilter.value;
      this.loadLogs();
    });

    categoryFilter?.addEventListener('change', () => {
      this.selectedCategory = categoryFilter.value;
      this.loadLogs();
    });

    searchInput?.addEventListener('input', () => {
      this.searchQuery = searchInput.value;
      clearTimeout((this as any)._searchTimeout);
      (this as any)._searchTimeout = setTimeout(() => this.loadLogs(), 300);
    });

    autoRefreshCheckbox?.addEventListener('change', () => {
      this.autoRefresh = autoRefreshCheckbox.checked;
      this.toggleAutoRefresh();
    });

    refreshBtn?.addEventListener('click', () => this.loadData());
  }

  private toggleAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    if (this.autoRefresh) {
      this.refreshInterval = setInterval(() => this.loadData(), 5000);
    }
  }

  private async loadData(): Promise<void> {
    // Skip if currently rate limited
    if (this.rateLimitedUntil && this.rateLimitedUntil > Date.now()) {
      return;
    }

    await Promise.all([this.loadMetrics(), this.loadCategories(), this.loadLogs()]);
    this.loading = false;
    this.render();
    this.afterRender();
  }

  private handleRateLimit(result: { rateLimited?: boolean; retryAfter?: number }): boolean {
    if (result.rateLimited) {
      this.rateLimitedUntil = Date.now() + (result.retryAfter || 60) * 1000;
      // Pause auto-refresh during rate limit
      if (this.autoRefresh && this.refreshInterval) {
        clearInterval(this.refreshInterval);
        // Resume after rate limit expires
        setTimeout(() => {
          if (this.autoRefresh) {
            this.rateLimitedUntil = null;
            this.toggleAutoRefresh();
          }
        }, (result.retryAfter || 60) * 1000);
      }
      this.render();
      this.afterRender();
      return true;
    }
    return false;
  }

  private async loadMetrics(): Promise<void> {
    const result = await api.getLogMetrics();
    if (this.handleRateLimit(result)) return;
    if (result.success && result.data) {
      this.metrics = result.data;
    }
  }

  private async loadCategories(): Promise<void> {
    const result = await api.getLogCategories();
    if (this.handleRateLimit(result)) return;
    if (result.success && result.data) {
      this.categories = result.data;
    }
  }

  private async loadLogs(): Promise<void> {
    const result = await api.getLogs({
      level: this.selectedLevel || undefined,
      category: this.selectedCategory || undefined,
      search: this.searchQuery || undefined,
      limit: 100,
    });

    if (this.handleRateLimit(result)) return;
    if (result.success && result.data) {
      this.logs = result.data;
    }

    this.render();
    this.afterRender();
  }
}

customElements.define('admin-logs', AdminLogs);
